import Backbone from 'backbone';

import {assertAndContinue} from 'Asserts';
import {ComponentsSequence as ComponentSequence} from 'components/Sequence';
import {Strand} from 'constants/MolbioConstants';
import Protein from 'molbio/proteins/Protein';
import * as sequtil from 'molbio/SeqUtil';

export interface SelectableAttributes {
  active: boolean;
  selected: boolean;
}

interface Scope<T extends Selectable> {
  allSelected: Array<T>;
  activeSelectable: T | null;
}

export interface SelectableOptions<T extends Selectable> {
  sequence?: ComponentSequence;
  scope?: Scope<T>;
}

type SelectOptions = Backbone.ModelSetOptions & {multi?: boolean; event?: string};

abstract class Selectable extends Backbone.Model {
  override defaults(): Partial<SelectableAttributes> {
    return {
      selected: false,
      active: false,
    };
  }
  selected(): boolean {
    return this.get('selected');
  }
  active(): boolean {
    return this.get('active');
  }
  select(options?: SelectOptions): void {
    // Forces a change events.
    this.set({selected: false}, {silent: true});
    this.set({selected: true}, options);
  }

  abstract start(): number;

  abstract end(): number;

  abstract strand(): Strand;
}

const defaultSelectionScope = {
  allSelected: [],
  activeSelectable: null,
};

function deselect<T extends Selectable>(options: SelectOptions, scope?: Scope<T>): void {
  if (scope == null) {
    scope = defaultSelectionScope;
  }
  if (scope.activeSelectable == null) {
    return;
  }
  scope.activeSelectable.set({active: false});
  for (const s of Array.from(scope.allSelected)) {
    s.set({selected: false}, options);
  }
  scope.allSelected = [];
}

abstract class RegionSelectable<Polymer extends ComponentSequence | Protein> extends Selectable {
  abstract uid(): string;

  layerStart(polymer?: Polymer): number {
    return this.start();
  }
  layerEnd(polymer?: Polymer): number {
    return this.end();
  }
  drawStart(polymer?: Polymer): number {
    return this.start();
  }
  drawEnd(polymer?: Polymer): number {
    return this.end();
  }
  length(polymer: Polymer): number {
    return sequtil.length(this.start(), this.end(), polymer.length());
  }
}

class Selectables<T extends Selectable> extends Backbone.Collection<T> {
  scope!: Scope<T>;

  override initialize(models: Array<T>, options?: SelectableOptions<T>): void {
    if (options == null) {
      options = {};
    }
    this.scope = options.scope || defaultSelectionScope;
    this.listenTo(this, 'change:selected', this.updateSelected.bind(this));
  }

  getCount(): number {
    return this.length;
  }

  updateSelected(model: T, selected: boolean, options: SelectOptions): void {
    if (options == null) {
      options = {};
    }
    if (!selected) {
      return;
    }
    this._doSelect(model, selected, options);
  }

  _doSelect(model: T, selected: boolean, options: SelectOptions): void {
    if (!selected) {
      return;
    }

    assertAndContinue(
      this.scope.activeSelectable != null || this.scope.allSelected.length === 0,
      `@scope.activeSelectable is null but @scope.allSelected has length ${this.scope.allSelected.length}`
    );

    if (this.scope.activeSelectable != null) {
      this.scope.activeSelectable.set({active: false}, options);
      if (!options.multi || this.scope.activeSelectable.constructor !== model.constructor) {
        for (const s of Array.from(this.scope.allSelected)) {
          if (s !== model) {
            s.set({selected: false}, options);
          }
        }
        this.scope.allSelected = [];
      }
    }

    if (!Array.from(this.scope.allSelected).includes(model)) {
      this.scope.allSelected.push(model);
    }
    this.scope.activeSelectable = model;
    model.set({active: true}, options);
  }

  deselect(options: SelectOptions = {}): void {
    deselect<T>(options, this.scope);
  }

  selected(): Array<T> {
    return this.scope.allSelected.slice();
  }
}

export {Selectable as Model, Selectables as Collection, RegionSelectable, deselect};
